import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';

function RankedList({ title, items, valueKey, valueLabel }) {
  return (
    <div className="bg-white rounded-xl shadow p-5 sm:p-6">
          <h3 className="font-bold text-[#1E1033] mb-3">{title}</h3>
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm">No data yet.</p>
      ) : (
        <ol className="space-y-2">
          {items.map((item, i) => (
            <li key={item.id} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs font-semibold text-gray-400 w-4 shrink-0">{i + 1}</span>
                <span className="text-sm text-[#1E1033] truncate">{item.title}</span>
              </div>
              <span className="text-sm font-semibold text-[#9333EA] shrink-0">
                {item[valueKey]} {valueLabel}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SourceColumn({ label, stats }) {
  return (
    <div className="flex-1 min-w-[140px]">
      <p className="text-xs font-medium text-gray-500 mb-2">{label}</p>
      <p className="text-2xl font-bold text-[#1E1033]">{stats.applications}</p>
      <p className="text-xs text-gray-500 mb-2">applications</p>
      <p className="text-sm text-[#1E1033]">
        {stats.hires} hired ·{' '}
        <span className="font-semibold">
          {stats.hire_rate === null ? '—' : `${stats.hire_rate}%`}
        </span>
      </p>
    </div>
  );
}

function JobAnalytics() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(5);

  // Mobile-only: sidebar drawer toggle (same pattern as other employer pages)
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const navigate = useNavigate();

  const fetchAnalytics = useCallback(async (limitValue) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/analytics/job', { params: { limit: limitValue } });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load job analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAnalytics(limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLimitChange = (e) => {
    const value = Number(e.target.value);
    setLimit(value);
    fetchAnalytics(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} activeTab="reports" />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
            {/* Mobile-only: nav drawer */}
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#1E1033]"
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? 'Hide' : 'Show'}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {showFiltersMobile && <EmployerSidebar user={user} activeTab="reports" mobile />}

            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block mb-1">Job Analytics</h2>
                <p className="text-gray-500 text-sm">How your job postings are performing</p>
              </div>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
              >
                <option value={5}>Top 5</option>
                <option value={10}>Top 10</option>
                <option value={20}>Top 20</option>
              </select>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 border border-red-100 p-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <p className="text-gray-500 text-sm">Loading...</p>
            ) : data ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <RankedList
                    title="Most Viewed Jobs"
                    items={data.most_viewed_jobs}
                    valueKey="views"
                    valueLabel="views"
                  />
                  <RankedList
                    title="Most Applied Jobs"
                    items={data.most_applied_jobs}
                    valueKey="applications"
                    valueLabel="applications"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl shadow p-5 sm:p-6">
                    <h3 className="font-bold text-[#1E1033] mb-1">Conversion Rate</h3>
                    <p className="text-xs text-gray-500 mb-3">Views that turned into applications</p>
                    <p className="text-3xl font-bold text-[#1E1033]">
                      {data.conversion_rate.rate === null ? '—' : `${data.conversion_rate.rate}%`}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {data.conversion_rate.applications} applications from {data.conversion_rate.views} views
                    </p>
                  </div>

                  <div className="bg-white rounded-xl shadow p-5 sm:p-6">
                    <h3 className="font-bold text-[#1E1033] mb-1">Time to Hire</h3>
                    <p className="text-xs text-gray-500 mb-3">Average days from application to joining</p>
                    <p className="text-3xl font-bold text-[#1E1033]">
                      {data.time_to_hire_days === null ? '—' : `${data.time_to_hire_days}d`}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow p-5 sm:p-6">
                  <h3 className="font-bold text-[#1E1033] mb-1">Source Performance</h3>
                  <p className="text-xs text-gray-500 mb-4">Agent-sourced vs. self-applied candidates</p>
                  <div className="flex flex-col sm:flex-row gap-6">
                    <SourceColumn label="Agent-sourced" stats={data.source_performance.agent_sourced} />
                    <div className="w-px bg-gray-100 hidden sm:block" />
                    <SourceColumn label="Self-applied" stats={data.source_performance.self_applied} />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobAnalytics;